// VENDOR CSS FIRST, AND IT HAS TO STAY THERE. Vite emits stylesheets in
// module-graph order, so whatever is imported first ends up EARLIEST in
// app.css — and between two rules of equal specificity, the later one wins.
// These imports used to sit below `App.vue`, which put Bootstrap's own
// `body { font-family: var(--bs-body-font-family) }` after App.vue's
// `body { font-family: "Roboto Condensed" }` and silently reverted the whole
// app to the system font stack. Bug report, 2026-09-14: "Did the font change
// for some reason on our notifications... our alert banner looks different."
// Vue CLI extracted vendor CSS into its own file and loaded it first, which
// is why the order never had to be thought about before the Vite move; this
// restores that arrangement explicitly. `cssImportOrder.test.js` guards it.
import "bootstrap/dist/css/bootstrap.min.css";
import 'bootstrap-icons/font/bootstrap-icons.css';
import "bootstrap";
// The app's own web fonts (Roboto Condensed, Lobster, Limelight), bundled so
// the first paint never waits on Google. See the file's header.
import "./assets/scss/fonts.css";

import { createApp } from "vue";
import App from "./App.vue";
import "./utils/screenPin.js"; // keeps the bug button at the real bottom of the screen (iOS cuts the page short)
import store from "./store";
import router from './router';
import VueClickAway from "vue3-click-away";
import * as Sentry from "@sentry/vue";
import { BrowserTracing } from "@sentry/tracing";
import { CaptureConsole } from "@sentry/integrations";
import { scrubBreadcrumb } from "./utils/scrubUrl.js";
import { beforeSendError, beforeSendTransaction, isTestBrowser } from "./utils/sentryBeforeSend.js";
import { handledErrorFilter } from "./utils/sentryHandled.js";
import VueLazyLoad from 'vue3-lazyload';
import './registerServiceWorker'
import axios from 'axios';
import { installNetworkHealth } from './utils/networkHealth.js';
import { setBuildInfo } from './assets/javascript/buildStamp.js';
import { trackUsage } from './utils/usage.js';
import { startUsageEvents } from './assets/javascript/usageEvents.js';

// One anonymous ping per open → Bug Desk's Visitors tab (house module).
trackUsage('cinemaroll');
// Which screens and actions get used (usageEvents.js).
startUsageEvents();

// Every network request gets a timeout, and a request nothing answers flips
// the app to its offline paths (lie-fi, bug report 2026-09-23). Before the
// app mounts, so no component can fire a request without it.
installNetworkHealth({ store, axios });

// The build stamp's values are read here, in the entry chunk, and handed
// over - never in the shared core chunk, which must stay byte-identical
// between builds that didn't change shared code (buildStamp.js).
setBuildInfo({ version: process.env.VUE_APP_VERSION, buildTime: process.env.VUE_APP_BUILD_TIME });

const app = createApp(App);

app.use(store);

// Movie Hat is a separate Firebase project with its own session. Watching it
// from start-up is what makes the connection survive a reload — otherwise the
// settings pane would offer to connect an account that is already connected.
store.dispatch('watchMovieHatAuth');

app.use(VueClickAway);

// rootMargin grows the IntersectionObserver's trigger zone so posters start
// loading ~1.5 screens before they scroll into view — on a normal scroll the
// image is already decoded by the time it appears, so the placeholder is
// effectively never seen. (Default is "0px", which only loads on entry and
// flashes the placeholder during fast scrolls.)
app.use(VueLazyLoad, {
  observerOptions: {
    rootMargin: '1200px 0px',
    threshold: 0,
  },
});

app.use(router);

// Sentry

const allowDevSentry = false;

// A test is not a user: a production build served from this machine, or any
// automated browser (Playwright etc.), even against the live site. See
// isTestBrowser in utils/sentryBeforeSend.js.
if (allowDevSentry || (process.env.NODE_ENV !== "development" && !isTestBrowser())) {
  Sentry.init({
    app,
    dsn: "https://25a3dc0387f04fd5923f226394a41e7d@o4504483013525504.ingest.sentry.io/4504642713944064",
    // The build stamp's version, so an issue says which build threw it and
    // Sentry can tell a regression from a straggler on an old tab.
    release: process.env.VUE_APP_VERSION ? `cinemaroll@${process.env.VUE_APP_VERSION}` : undefined,
    integrations: [
      // Every console.error becomes an event (2026-10-08): the caught failures
      // that used to die in a console nobody reads. utils/sentryHandled.js
      // budgets and groups them so they can't spend the error quota.
      new CaptureConsole({ levels: ["error"] }),
      new BrowserTracing({
        // Hash routing means window.location.pathname is always "/", so
        // without the router every transaction was named "/" and a Sentry
        // alert couldn't say which screen it came from (the 2026-10-06
        // "N+1 API Call" email). With it, transactions carry the route name.
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracePropagationTargets: ["localhost", "surge", /^\//],
      }),
    ],
    // A quarter of page loads carry a full performance trace (audit,
    // 2026-10-06): plenty for the N+1 / large-payload detectors, a quarter
    // of the upload from phones and of the Sentry quota. Errors are still
    // every one.
    tracesSampleRate: 0.25,
    sampleRate: 1.0,
    attachStacktrace: true,
    maxValueLength: 8000,
    // No credentials leave for Sentry: Firebase and TMDB take theirs in the
    // query string, and traced fetches and breadcrumbs carried them in full
    // (see utils/scrubUrl.js, 2026-10-06). Transactions are also tagged with
    // the cause of any request burst (utils/burstLabel.js, 2026-10-19).
    beforeSend: handledErrorFilter(beforeSendError),
    beforeSendTransaction,
    beforeBreadcrumb: scrubBreadcrumb
  });
}

app.mount("#app");
